"use client";

import { create } from "zustand";

/** A proposal row's venue, and the event whose grid it is for. */
export interface ProposalSearchRequest {
  eventId: string;
  venue: string;
}

interface CommandPaletteStoreState {
  isOpen: boolean;
  /**
   * How the palette was last opened. Recorded because whether anyone actually
   * presses the hotkey — versus clicking the magnifying glass — is the main
   * open risk on this feature, and the split is only knowable from here.
   */
  openedFrom: "hotkey" | "icon" | null;
  open: (source: "hotkey" | "icon") => void;
  close: () => void;
  toggle: (source: "hotkey" | "icon") => void;

  /**
   * A search the palette could not answer, waiting for the chat composer.
   *
   * Carried here rather than in a `?q=` param because the palette is reachable
   * from `/dashboard/chat` itself, and pushing a route you are already on does
   * not hand a new param to an already-mounted client tree: the composer stayed
   * empty and the query was lost with no error. Verified in the browser.
   */
  pendingChatQuery: string | null;
  setPendingChatQuery: (query: string) => void;
  /** Clears it once the composer has taken it, so a later visit starts empty. */
  consumePendingChatQuery: () => void;

  /**
   * The venue a proposal row last asked the proposals grid to search for.
   *
   * The row's `?venue=` link covers arriving from anywhere else. It cannot cover
   * picking the venue the grid is already open on: pushing the URL you are on
   * re-renders the page with the same search param, so a box the user has typed
   * over since keeps what they typed.
   *
   * A fresh object per selection, so picking the same venue twice is two
   * requests. Never cleared: the grid ignores whatever was here when it
   * mounted, because the URL it mounted with already carries that venue, and
   * that is what stops an old request from overwriting a later visit's search.
   */
  proposalSearchRequest: ProposalSearchRequest | null;
  requestProposalSearch: (eventId: string, venue: string) => void;
}

/**
 * Open/closed state for the command palette.
 *
 * A store rather than local state because two unrelated components drive it:
 * the global hotkey hook and the sidebar's magnifying glass, which sit on
 * opposite sides of the tree. Not persisted — the palette always starts closed.
 *
 * @returns The open state and its controls, plus the handoffs to the chat
 *   composer and the proposals grid.
 * @throws Never.
 */
export const useCommandPaletteStore = create<CommandPaletteStoreState>((set, get) => ({
  isOpen: false,
  openedFrom: null,
  open: (openedFrom) => set({ isOpen: true, openedFrom }),
  close: () => set({ isOpen: false }),
  toggle: (source) => (get().isOpen ? set({ isOpen: false }) : set({ isOpen: true, openedFrom: source })),

  pendingChatQuery: null,
  setPendingChatQuery: (pendingChatQuery) => set({ pendingChatQuery }),
  consumePendingChatQuery: () => set({ pendingChatQuery: null }),

  proposalSearchRequest: null,
  requestProposalSearch: (eventId, venue) => set({ proposalSearchRequest: { eventId, venue } }),
}));
